import { useMemo, useState, type ReactNode } from "react";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  FileQuestion,
  Search,
  ShieldCheck,
} from "lucide-react";

import { CmsPublicEntry } from "@/marketing/CmsPublicEntry";
import {
  helpArticleByPath,
  helpArticles,
  helpCategories,
  searchHelpArticles,
} from "@/marketing/documentation/catalog";
import {
  DOCUMENTATION_LAST_REVIEWED,
  type HelpArticle,
  type HelpCategoryId,
} from "@/marketing/documentation/types";

const aggregatePaths = new Set(["/help", "/faq", "/how-to", "/resources"]);

function cleanPath(path: string) {
  return path.replace(/\/+$/, "") || "/";
}

export function documentationPublicPath(path: string) {
  const clean = cleanPath(path);
  return aggregatePaths.has(clean) || Boolean(helpArticleByPath(clean));
}

export function DocumentationPublicPage({ path }: { path: string }) {
  const clean = cleanPath(path);
  let content: ReactNode;
  if (clean === "/help") content = <HelpCentre />;
  else if (clean === "/faq") content = <FaqPage />;
  else if (clean === "/how-to") content = <HowToPage />;
  else if (clean === "/resources") content = <ResourcesPage />;
  else {
    const article = helpArticleByPath(clean);
    content = article
      ? <CmsPublicEntry path={clean} fallback={<HelpArticlePage article={article} />} />
      : null;
  }
  return <DocumentationShell>{content}</DocumentationShell>;
}

function DocumentationShell({ children }: { children: ReactNode }) {
  return (
    <div className="marketing-shell min-h-screen bg-surface-paper text-on-paper">
      <header className="border-b border-line-dark bg-surface-ink text-on-ink-strong">
        <div className="mx-auto flex min-h-20 max-w-7xl items-center justify-between gap-5 px-5 py-4 md:px-8">
          <a className="inline-flex items-center gap-3" href="/" aria-label="RAQOZ home">
            <span className="grid size-10 place-items-center rounded-2xl bg-teal-core font-black text-white">R</span>
            <strong className="tracking-[.22em]">RAQOZ</strong>
          </a>
          <nav aria-label="Documentation navigation" className="hidden items-center gap-6 text-sm text-on-ink lg:flex">
            <a className="hover:text-teal-bright" href="/resources">Resources</a>
            <a className="hover:text-teal-bright" href="/help">Help centre</a>
            <a className="hover:text-teal-bright" href="/how-to">How-to</a>
            <a className="hover:text-teal-bright" href="/faq">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <a className="px-3 py-2 text-sm font-semibold text-on-ink" href="/login">Sign in</a>
            <a className="marketing-button-secondary-dark hidden sm:inline-flex" href="/signup">Start free</a>
          </div>
        </div>
      </header>
      <main>{children}</main>
      <footer className="border-t border-line-dark bg-surface-ink px-5 py-10 text-on-ink md:px-8">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-6 sm:flex-row">
          <div><strong className="tracking-[.2em] text-on-ink-strong">RAQOZ</strong><p className="mt-3 text-sm text-on-ink-muted">Every purchase, accountable.</p></div>
          <nav aria-label="Documentation footer" className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            <a className="hover:text-teal-bright" href="/security">Security</a>
            <a className="hover:text-teal-bright" href="/privacy">Privacy</a>
            <a className="hover:text-teal-bright" href="/terms">Terms</a>
            <a className="hover:text-teal-bright" href="/contact">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function PageIntro({
  eyebrow,
  title,
  text,
  children,
}: {
  eyebrow: string;
  title: string;
  text: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-surface-paper px-5 py-16 text-on-paper md:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <p className="marketing-eyebrow-light">{eyebrow}</p>
        <h1 className="mt-5 max-w-5xl font-serif text-5xl leading-[1.03] tracking-[-.045em] sm:text-6xl">{title}</h1>
        <p className="mt-7 max-w-3xl text-lg leading-8 text-on-paper-muted">{text}</p>
        {children}
      </div>
    </section>
  );
}

function HelpCentre() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<HelpCategoryId | "all">("all");
  const results = useMemo(
    () => searchHelpArticles(query, category === "all" ? undefined : category),
    [query, category],
  );
  return (
    <PageIntro
      eyebrow="HELP CENTRE · 51 VERIFIED ARTICLES"
      title="Find the accountable next step."
      text="Search role, workflow, administration, controls, imports, troubleshooting and security guidance. Product records remain the source of truth; these articles do not replace policy, legal advice, audit work or professional judgement."
    >
      <section aria-label="Search help" className="mt-10 rounded-[1.7rem] border border-line-light bg-surface-card p-5 md:p-7">
        <label className="block font-semibold" htmlFor="help-search">Search all help articles</label>
        <div className="relative mt-3">
          <Search aria-hidden="true" className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-on-paper-muted" />
          <input
            id="help-search"
            className="min-h-12 w-full rounded-xl border border-line-light bg-white py-3 pl-12 pr-4 outline-none focus:border-teal-deep"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Try invoice matching, approver, imports or Control Passport"
            type="search"
            value={query}
          />
        </div>
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter help by category">
          <FilterButton active={category === "all"} onClick={() => setCategory("all")}>All</FilterButton>
          {helpCategories.map((item) => (
            <FilterButton active={category === item.id} key={item.id} onClick={() => setCategory(item.id)}>{item.label}</FilterButton>
          ))}
        </div>
        <p className="mt-4 text-sm text-on-paper-muted" aria-live="polite">{results.length} {results.length === 1 ? "article" : "articles"} found</p>
      </section>

      {!query && category === "all" && (
        <nav aria-label="Help categories" className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {helpCategories.map((item) => {
            const count = helpArticles.filter((article) => article.category === item.id).length;
            return (
              <a className="rounded-2xl border border-line-light bg-surface-card p-5 transition hover:border-teal-deep" href={"#help-" + item.id} key={item.id}>
                <span className="text-xs font-black tracking-[.12em] text-teal-deep">{count} ARTICLES</span>
                <strong className="mt-3 block font-serif text-2xl">{item.label}</strong>
                <span className="mt-2 block text-sm leading-6 text-on-paper-muted">{item.description}</span>
              </a>
            );
          })}
        </nav>
      )}

      <div className="mt-12 grid gap-10">
        {helpCategories.map((item) => {
          const articles = results.filter((article) => article.category === item.id);
          if (!articles.length) return null;
          return (
            <section id={"help-" + item.id} key={item.id}>
              <h2 className="font-serif text-3xl">{item.title}</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-on-paper-muted">{item.description}</p>
              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {articles.map((article) => <ArticleLink article={article} key={article.path} />)}
              </div>
            </section>
          );
        })}
        {!results.length && (
          <section className="rounded-2xl border border-line-light bg-surface-card p-8 text-center">
            <FileQuestion className="mx-auto size-8 text-teal-deep" />
            <h2 className="mt-4 font-serif text-3xl">No matching article</h2>
            <p className="mt-3 text-sm text-on-paper-muted">Try a shorter term or clear the category filter.</p>
          </section>
        )}
      </div>
      <SupportStrip />
    </PageIntro>
  );
}

function FilterButton({ active, children, onClick }: { active: boolean; children: ReactNode; onClick: () => void }) {
  return <button aria-pressed={active} className={active ? "rounded-full bg-teal-deep px-4 py-2 text-sm font-semibold text-white" : "rounded-full border border-line-light bg-white px-4 py-2 text-sm font-semibold"} onClick={onClick} type="button">{children}</button>;
}

function ArticleLink({ article }: { article: HelpArticle }) {
  return (
    <a className="group flex items-start justify-between gap-5 rounded-2xl border border-line-light bg-surface-card p-5 transition hover:border-teal-deep" href={article.path}>
      <span><strong className="font-semibold">{article.title}</strong><span className="mt-2 block text-sm leading-6 text-on-paper-muted">{article.summary}</span></span>
      <ArrowRight className="mt-1 size-4 shrink-0 text-teal-deep transition group-hover:translate-x-1" />
    </a>
  );
}

function HelpArticlePage({ article }: { article: HelpArticle }) {
  const category = helpCategories.find((item) => item.id === article.category);
  return (
    <article className="bg-surface-paper px-5 py-16 text-on-paper md:px-8 lg:py-24">
      <div className="mx-auto max-w-4xl">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm text-on-paper-muted">
          <a className="hover:text-teal-deep" href="/help">Help centre</a>
          <span aria-hidden="true">/</span>
          <a className="hover:text-teal-deep" href={"/help#help-" + article.category}>{category?.label}</a>
        </nav>
        <p className="marketing-eyebrow-light mt-8">{category?.label} guidance</p>
        <h1 className="mt-5 font-serif text-5xl leading-[1.03] tracking-[-.045em] sm:text-6xl">{article.title}</h1>
        <p className="mt-7 text-xl leading-8 text-on-paper-muted">{article.summary}</p>
        <dl className="mt-8 grid gap-4 rounded-2xl border border-line-light bg-surface-card p-6 sm:grid-cols-2">
          <div><dt className="text-xs font-black tracking-[.12em] text-teal-deep">FOR</dt><dd className="mt-2 text-sm leading-6">{article.audience.join(", ")}</dd></div>
          <div><dt className="text-xs font-black tracking-[.12em] text-teal-deep">OUTCOME</dt><dd className="mt-2 text-sm leading-6">{article.outcome}</dd></div>
        </dl>
        <ArticleSection title="Before you begin">
          <CheckList items={article.prerequisites} />
        </ArticleSection>
        <ArticleSection title="Guidance">
          <ol className="grid gap-4">
            {article.guidance.map((step, index) => (
              <li className="grid grid-cols-[auto_1fr] gap-4 rounded-2xl border border-line-light bg-surface-card p-5" key={step}>
                <span className="grid size-9 place-items-center rounded-full bg-teal-wash font-black text-teal-deep">{index + 1}</span>
                <p className="pt-1 leading-7 text-on-paper-muted">{step}</p>
              </li>
            ))}
          </ol>
        </ArticleSection>
        <ArticleSection title={article.evidence[0]?.startsWith("This reference") ? "Record impact" : "Evidence RAQOZ retains"}>
          <CheckList items={article.evidence} />
        </ArticleSection>
        <ArticleSection title="Common issues">
          <div className="grid gap-3">
            {article.issues.map((issue) => (
              <details className="rounded-2xl border border-line-light bg-surface-card p-5" key={issue.problem}>
                <summary className="cursor-pointer font-semibold">{issue.problem}</summary>
                <p className="mt-3 text-sm leading-7 text-on-paper-muted">{issue.resolution}</p>
              </details>
            ))}
          </div>
        </ArticleSection>
        {article.related.length > 0 && (
          <ArticleSection title="Related articles">
            <div className="grid gap-3 sm:grid-cols-2">
              {article.related.map((path) => {
                const related = helpArticleByPath(path);
                return related ? <ArticleLink article={related} key={path} /> : null;
              })}
            </div>
          </ArticleSection>
        )}
        <p className="mt-12 border-t border-line-light pt-5 text-xs text-on-paper-muted">Last reviewed {DOCUMENTATION_LAST_REVIEWED}. Review against the current production release before relying on exact screen behavior.</p>
      </div>
    </article>
  );
}

function ArticleSection({ title, children }: { title: string; children: ReactNode }) {
  return <section className="mt-12"><h2 className="font-serif text-3xl">{title}</h2><div className="mt-5">{children}</div></section>;
}

function CheckList({ items }: { items: string[] }) {
  return <ul className="grid gap-3">{items.map((item) => <li className="flex gap-3 leading-7 text-on-paper-muted" key={item}><CheckCircle2 className="mt-1 size-5 shrink-0 text-teal-deep" />{item}</li>)}</ul>;
}

const faqGroups = [
  {
    title: "Product and getting started",
    items: [
      ["What is RAQOZ?", "RAQOZ is an evidence-first procurement operating system that connects request, approval, sourcing, order, receipt, invoice and control evidence."],
      ["Where should we start?", "Set up the organisation and roles, then run one low-risk purchase end to end before broad rollout."],
      ["Does RAQOZ execute bank payments?", "No. RAQOZ records governed payment states and evidence; bank execution happens in your authorised payment process."],
    ],
  },
  {
    title: "Roles and access",
    items: [
      ["Which roles are supported?", "Organisation administrator, requester, approver, procurement, receiver, finance and auditor."],
      ["Can a requester approve the same request?", "The accountable workflow separates request and approval duties. Assignment and server-side permissions govern the decision."],
      ["Why is a screen missing?", "Confirm the active organisation, assigned role and any record-specific assignment."],
    ],
  },
  {
    title: "Workflows and controls",
    items: [
      ["What does a Control Passport prove?", "It shows the purchase's control identity, policy context, mapped evidence, exceptions and provenance. It is not a compliance certificate or audit opinion."],
      ["Can a control be tested before activation?", "Configured policies support no-write simulation so proposed behavior can be reviewed before activation."],
      ["How are justified exceptions handled?", "Use bounded maker-checker waivers or governed overrides where supported; the original result remains visible."],
    ],
  },
  {
    title: "Suppliers",
    items: [
      ["What is supplier qualification?", "It is the buying organisation's recorded status and rationale for a supplier in its own context, not a universal endorsement."],
      ["What if we select a non-lowest quotation?", "Record the commercial or operational rationale and satisfy any additional active approval or waiver requirement."],
    ],
  },
  {
    title: "Imports and migration",
    items: [
      ["What can be imported?", "The governed import workflow covers suppliers, inventory, control matrices and historical purchase evidence."],
      ["Does imported history create live approvals or receipts?", "No. Historical imports remain provenance-labelled evidence-only records and cannot create live approval, receipt or payment states."],
    ],
  },
  {
    title: "Security and privacy",
    items: [
      ["How is access scoped?", "Membership, supported roles, record assignment and server-side data controls scope access to an organisation."],
      ["Can confidential documents be emailed to general support?", "No. Do not send passwords, one-time codes, bank credentials or confidential procurement documents through a general support message."],
      ["Does RAQOZ claim a certification?", "No certification or statutory compliance conclusion is implied by the help centre or product evidence."],
    ],
  },
  {
    title: "Pricing and implementation",
    items: [
      ["Where is current pricing?", "Use the pricing page and its availability labels. Commercial terms and entitlements in your organisation remain authoritative."],
      ["Can we migrate everything first?", "A smaller first-purchase rollout is safer. Validate the operating path, then import governed reference data and evidence."],
    ],
  },
  {
    title: "Support",
    items: [
      ["How do we ask for help?", "Use the contact or support link and include the affected organisation, record ID, expected state, actual state and time—without sending secrets or confidential files."],
      ["Is a response-time SLA promised here?", "No. Any contractual support commitment must come from the applicable service agreement, not this FAQ."],
    ],
  },
] as const;

function FaqPage() {
  return (
    <PageIntro eyebrow="FREQUENTLY ASKED QUESTIONS" title="Clear boundaries, practical answers." text="These answers summarize released workflows and important limits. Exact entitlements, policy decisions and contractual terms remain authoritative.">
      <div className="mt-12 grid gap-10">
        {faqGroups.map((group) => <section key={group.title}><h2 className="font-serif text-3xl">{group.title}</h2><div className="mt-4 grid gap-3">{group.items.map(([question, answer]) => <details className="rounded-2xl border border-line-light bg-surface-card p-5" key={question}><summary className="cursor-pointer font-semibold">{question}</summary><p className="mt-3 text-sm leading-7 text-on-paper-muted">{answer}</p></details>)}</div></section>)}
      </div>
      <SupportStrip />
    </PageIntro>
  );
}

const howToCategories: HelpCategoryId[] = ["start", "workflows", "administration", "controls", "imports", "troubleshooting", "security"];

function HowToPage() {
  return (
    <PageIntro eyebrow="HOW-TO INDEX" title="Choose the task you need to complete." text="This is a topic index, not one artificial sequence. Open the role, workflow or control article that matches the record in front of you.">
      <div className="mt-12 grid gap-10">
        {howToCategories.map((categoryId) => {
          const category = helpCategories.find((item) => item.id === categoryId);
          const articles = helpArticles.filter((article) => article.category === categoryId);
          return <section key={categoryId}><h2 className="font-serif text-3xl">{category?.title}</h2><div className="mt-5 grid gap-3 md:grid-cols-2">{articles.map((article) => <ArticleLink article={article} key={article.path} />)}</div></section>;
        })}
      </div>
    </PageIntro>
  );
}

function ResourcesPage() {
  const cards = [
    ["Use the product", "Search 51 verified help articles for roles, workflows, administration, controls, imports, troubleshooting and security.", "/help", "Open help centre"],
    ["Complete a task", "Browse task-oriented how-to links that point directly to the canonical help article.", "/how-to", "Open how-to index"],
    ["Check a boundary", "Read concise answers about access, suppliers, imports, security, pricing, implementation and support.", "/faq", "Open FAQ"],
    ["Use practical resources", "Open browser tools, editable templates and procurement guides alongside product documentation.", "/tools", "Open free tools"],
  ] as const;
  return (
    <PageIntro eyebrow="RESOURCE LIBRARY" title="Documentation and practical procurement resources, connected." text="Start with the product task, then use the relevant tool, template or guide without losing the accountable source record.">
      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {cards.map(([title, text, href, label]) => <article className="rounded-[1.7rem] border border-line-light bg-surface-card p-7" key={title}><BookOpen className="size-6 text-teal-deep" /><h2 className="mt-5 font-serif text-3xl">{title}</h2><p className="mt-3 text-sm leading-6 text-on-paper-muted">{text}</p><a className="marketing-text-link-light mt-6" href={href}>{label}<ArrowRight className="size-4" /></a></article>)}
      </div>
      <section className="mt-12 rounded-[2rem] bg-surface-ink p-8 text-on-ink-strong">
        <ShieldCheck className="size-7 text-teal-bright" />
        <h2 className="mt-5 font-serif text-4xl">Important product boundaries</h2>
        <ul className="mt-5 grid gap-3 text-sm leading-6 text-on-ink">
          <li>RAQOZ records payment approval and evidence; it does not execute bank payments.</li>
          <li>Historical imports are evidence only and do not create live workflow states.</li>
          <li>Product evidence does not guarantee compliance, certification or an audit outcome.</li>
          <li>Public API, direct bank and unverified external-integration claims remain outside this documentation.</li>
        </ul>
      </section>
    </PageIntro>
  );
}

function SupportStrip() {
  return (
    <section className="mt-12 rounded-[1.7rem] bg-surface-ink p-8 text-on-ink-strong">
      <ShieldCheck className="size-7 text-teal-bright" />
      <h2 className="mt-5 font-serif text-3xl">Still need help?</h2>
      <p className="mt-3 max-w-3xl text-sm leading-6 text-on-ink">Include the organisation, affected record ID, expected state, actual state and time. Do not email passwords, one-time codes, bank credentials or confidential procurement documents.</p>
      <a className="marketing-button-primary-dark mt-6" href="mailto:cyclotrontechnologies@gmail.com?subject=RAQOZ%20help">Contact support <ArrowRight className="size-4" /></a>
    </section>
  );
}
